import { type FormEvent, useState } from 'react';
import { Alert, Button, Checkbox, Field, Select, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { errorMessage } from '../../lib/errors';
import type { Billing } from '../people/data';
import { type LicenseSummary, useLicenseForm } from './data';
import { PersonPicker } from './PersonPicker';

export interface LicenseValues {
  name: string;
  platformId: string | null;
  ownerId: string | null;
  billing: Billing;
  baseCost: number;
  shared: boolean;
  renewalDate: string | null;
  seatsPurchased: number | null;
  notes: string | null;
}

/** Lo que el administrador de la plataforma carga en la suya (el resto, quien gestiona licencias). */
const OWNER_FIELDS: ReadonlyArray<keyof LicenseValues> = ['baseCost', 'renewalDate', 'seatsPurchased', 'notes'];

/**
 * Alta o edición de un tipo de licencia. Al editar se envían solo los campos que cambiaron; con `ownerOnly`, solo se
 * pueden cambiar el costo, la renovación, los puestos y las notas.
 */
export function LicenseForm({
  initial,
  ownerOnly = false,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: LicenseSummary;
  ownerOnly?: boolean;
  submitLabel: string;
  onSubmit: (values: Partial<LicenseValues>) => Promise<void>;
  onCancel: () => void;
}) {
  const form = useLicenseForm();
  const [name, setName] = useState(initial?.name ?? '');
  const [platformId, setPlatformId] = useState(initial?.platformId ?? '');
  const [owner, setOwner] = useState<{ id: string; fullName: string } | null>(initial?.ownerId ? { id: initial.ownerId, fullName: initial.owner ?? '—' } : null);
  const [billing, setBilling] = useState<Billing>(initial?.billing ?? 'mensual');
  const [baseCost, setBaseCost] = useState(initial ? String(initial.baseCost) : '');
  const [shared, setShared] = useState(initial?.shared ?? false);
  const [renewalDate, setRenewalDate] = useState(initial?.renewalDate ?? '');
  const [seats, setSeats] = useState(initial?.seatsPurchased != null ? String(initial.seatsPurchased) : '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const values: LicenseValues = {
      name: name.trim(),
      platformId: platformId || null,
      ownerId: owner?.id ?? null,
      billing,
      baseCost: Number(baseCost.replace(',', '.')),
      shared,
      renewalDate: renewalDate || null,
      seatsPurchased: seats.trim() ? Number(seats) : null,
      notes: notes.trim() || null,
    };
    let body: Partial<LicenseValues> = values;
    if (initial) {
      const before = { ...initial, notes: initial.notes ?? null } as unknown as LicenseValues;
      body = Object.fromEntries(Object.entries(values).filter(([k, v]) => (!ownerOnly || OWNER_FIELDS.includes(k as keyof LicenseValues)) && before[k as keyof LicenseValues] !== v));
      if (Object.keys(body).length === 0) return onCancel();
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(body);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const locked = ownerOnly;
  return (
    <form onSubmit={submit} className="space-y-4">
      {error && <Alert>{error}</Alert>}
      {ownerOnly && <Alert tone="info">{t('li.solo_costos')}</Alert>}
      <Field id="li-nombre" label={t('li.campo_nombre')} required minLength={2} maxLength={120} disabled={locked} value={name} onChange={(e) => setName(e.target.value)} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Select id="li-plataforma" label={t('li.campo_plataforma')} disabled={locked} value={platformId} onChange={(e) => setPlatformId(e.target.value)}>
          <option value="">{t('li.sin_plataforma')}</option>
          {form.data?.platforms.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        {locked ? (
          <div className="space-y-1">
            <p className="text-sm font-medium text-slate-700">{t('li.campo_administrador')}</p>
            <p className="text-sm text-slate-900">{owner?.fullName ?? '—'}</p>
          </div>
        ) : (
          <PersonPicker id="li-administrador" label={t('li.campo_administrador')} value={owner} onChange={setOwner} />
        )}
      </div>
      <fieldset className="space-y-2" disabled={locked}>
        <legend className="text-sm font-medium text-slate-700">{t('li.campo_modalidad')}</legend>
        <div className="flex flex-wrap gap-2">
          {(['mensual', 'anual'] as const).map((b) => (
            <label key={b} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${billing === b ? 'border-marca bg-marca/5' : 'border-slate-200 hover:bg-slate-50'}`}>
              <input type="radio" name="li-modalidad" value={b} checked={billing === b} onChange={() => setBilling(b)} className="accent-marca" />
              {t(b === 'mensual' ? 'li.mensual' : 'li.anual')}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field
          id="li-costo"
          type="number"
          min={0}
          step="any"
          label={billing === 'mensual' ? t('li.campo_costo_mes') : t('li.campo_costo_anio')}
          required
          value={baseCost}
          onChange={(e) => setBaseCost(e.target.value)}
        />
        <Field id="li-puestos" type="number" min={0} step={1} label={t('li.campo_puestos')} value={seats} onChange={(e) => setSeats(e.target.value)} />
        <Field id="li-renovacion" type="date" label={t('li.campo_renovacion')} required={billing === 'anual'} value={renewalDate} onChange={(e) => setRenewalDate(e.target.value)} />
      </div>
      <p className="-mt-2 text-xs text-slate-500">{t('li.puestos_ayuda')}</p>
      <Checkbox checked={shared} disabled={locked} onChange={(e) => setShared(e.target.checked)} label={t('li.campo_compartida')} hint={t('li.compartida_ayuda')} />
      <Textarea id="li-notas" label={t('li.campo_notas')} rows={2} maxLength={2_000} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="flex gap-2">
        <Button type="submit" disabled={busy || name.trim().length < 2 || baseCost === '' || (billing === 'anual' && !renewalDate)}>
          {submitLabel}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          {t('comun.cancelar')}
        </Button>
      </div>
    </form>
  );
}
